import { useCallback, useEffect, useRef, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
export const ACTIVATION_RETRY = { INTERVAL_MS: 300, TIMEOUT_MS: 10_000 } as const;

type ActivationResult = "activated" | "failed";

export function activateWaitingServiceWorker(
  registration: ServiceWorkerRegistration,
  options: {
    signal?: AbortSignal;
    serviceWorkerContainer?: ServiceWorkerContainer;
  } = {},
): Promise<ActivationResult> {
  const container = options.serviceWorkerContainer ?? navigator.serviceWorker;

  return new Promise((resolve) => {
    let settled = false;
    let retryTimer: ReturnType<typeof setInterval> | undefined;
    let timeoutTimer: ReturnType<typeof setTimeout> | undefined;

    const finish = (result: ActivationResult) => {
      if (settled) return;
      settled = true;
      if (retryTimer) clearInterval(retryTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
      container.removeEventListener("controllerchange", handleControllerChange);
      options.signal?.removeEventListener("abort", handleAbort);
      resolve(result);
    };

    const activated = () =>
      !registration.waiting &&
      !!container.controller &&
      registration.active === container.controller;

    const requestActivation = () => {
      if (activated()) {
        finish("activated");
        return;
      }
      registration.waiting?.postMessage({ type: "SKIP_WAITING" });
    };

    const handleControllerChange = () => {
      if (activated()) finish("activated");
    };
    const handleAbort = () => finish("failed");

    if (options.signal?.aborted) {
      finish("failed");
      return;
    }

    container.addEventListener("controllerchange", handleControllerChange);
    options.signal?.addEventListener("abort", handleAbort, { once: true });
    requestActivation();
    if (settled) return;

    retryTimer = setInterval(requestActivation, ACTIVATION_RETRY.INTERVAL_MS);
    timeoutTimer = setTimeout(() => finish(activated() ? "activated" : "failed"), ACTIVATION_RETRY.TIMEOUT_MS);
  });
}

type PromptState = "ready" | "updating" | "stalled" | "failed";

export function PwaUpdatePrompt() {
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const updateCheckTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activationAbortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const [promptState, setPromptState] = useState<PromptState>("ready");
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    // The plugin otherwise reloads on "controlling" even when its update
    // function receives false. Own navigation here so only a confirmed,
    // explicitly accepted handoff reloads this page, and only once.
    onNeedReload() {},
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;
      if (updateCheckTimerRef.current) clearInterval(updateCheckTimerRef.current);
      updateCheckTimerRef.current = setInterval(() => {
        void registration.update();
      }, UPDATE_CHECK_INTERVAL_MS);
    },
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      activationAbortRef.current?.abort();
      if (updateCheckTimerRef.current) clearInterval(updateCheckTimerRef.current);
    };
  }, []);

  useEffect(() => {
    // A successful worker handoff naturally flips needRefresh to false before
    // controllerchange has always reached this component. Do not abort that
    // in-flight activation or the user sees a dead retry/update action and no
    // reload. Only a newly available update should reset the visible prompt.
    if (needRefresh) setPromptState("ready");
  }, [needRefresh]);

  const attemptActivation = useCallback(async () => {
    activationAbortRef.current?.abort();
    const abortController = new AbortController();
    activationAbortRef.current = abortController;
    setPromptState("updating");

    try {
      // Ask vite-plugin-pwa to message its worker too, but own the reload so it
      // can happen only after the new worker is confirmed as controller.
      void updateServiceWorker(false);
      const registration =
        registrationRef.current ??
        (await navigator.serviceWorker.getRegistration()) ??
        null;
      if (registration) registrationRef.current = registration;
      // The waiting worker may already have activated between the plugin's
      // update request and this check. Let the handshake confirm that state.
      if (!registration) {
        if (mountedRef.current && !abortController.signal.aborted) setPromptState("failed");
        return;
      }

      const result = await activateWaitingServiceWorker(registration, {
        signal: abortController.signal,
      });
      if (!mountedRef.current || abortController.signal.aborted) return;
      if (result === "activated") {
        setNeedRefresh(false);
        setPromptState("ready");
        window.location.reload();
      } else {
        setPromptState("stalled");
      }
    } catch {
      if (mountedRef.current && !abortController.signal.aborted) setPromptState("failed");
    }
  }, [setNeedRefresh, updateServiceWorker]);

  const dismiss = () => {
    activationAbortRef.current?.abort();
    setPromptState("ready");
    setNeedRefresh(false);
  };

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="pwa-update-prompt"
      className="fixed bottom-4 left-1/2 z-[100] flex w-[min(92vw,28rem)] -translate-x-1/2 items-center gap-3 rounded-md border border-border bg-background px-4 py-3 shadow-lg"
    >
      {promptState === "updating" ? (
        <span className="flex-1 text-[13px] text-foreground" data-testid="text-pwa-updating">
          Updating…
        </span>
      ) : promptState === "stalled" || promptState === "failed" ? (
        <>
          <span className="flex-1 text-[13px] text-foreground" data-testid="text-pwa-stalled">
             The update could not finish. Reload the page to continue. Unsaved changes may be lost.
          </span>
          <button
            type="button"
            data-testid="button-pwa-force-reload"
            title="Reload without confirming the update. Unsaved changes may be lost."
            onClick={() => window.location.reload()}
            className="rounded-md bg-brand-sage px-3 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-sage-deep"
          >
            Reload
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 text-[13px] text-foreground">
            A new version of InControl is available.
          </span>
          <button
            type="button"
            data-testid="button-pwa-dismiss"
            onClick={dismiss}
            className="rounded-md px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Later
          </button>
          <button
            type="button"
            data-testid="button-pwa-update"
            onClick={() => void attemptActivation()}
            className="rounded-md bg-brand-sage px-3 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-sage-deep"
          >
            Reload
          </button>
        </>
      )}
    </div>
  );
}

export default PwaUpdatePrompt;